import { KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';

interface ChatInputProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  loading: boolean;
  usageRemaining: number | null;
  disabled: boolean;
}

export function ChatInput({
  value,
  onChange,
  onSend,
  loading,
  usageRemaining,
  disabled,
}: ChatInputProps) {
  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && !loading && value.trim().length > 0) {
        onSend();
      }
    }
  }

  const limitHit = usageRemaining === 0;
  const sendDisabled = disabled || loading || limitHit || value.trim().length === 0;

  return (
    <div
      style={{
        padding: '14px 20px',
        background: 'var(--bg-primary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-end',
          maxWidth: '880px',
          margin: '0 auto',
        }}
      >
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || limitHit}
          rows={1}
          placeholder={limitHit ? 'Daily limit reached' : 'Ask a PM question...'}
          style={{
            flex: 1,
            resize: 'none',
            minHeight: '44px',
            maxHeight: '160px',
            padding: '12px 14px',
            borderRadius: '10px',
            border: '1px solid var(--border)',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
            fontSize: '14px',
            lineHeight: 1.5,
            outline: 'none',
            overflow: value ? 'auto' : 'hidden',
          }}
        />
        <button
          onClick={onSend}
          disabled={sendDisabled}
          style={{
            height: '44px',
            minWidth: '88px',
            padding: '0 18px',
            borderRadius: '10px',
            border: 'none',
            background: sendDisabled ? 'var(--bg-card)' : 'var(--accent)',
            color: sendDisabled ? 'var(--text-muted)' : '#0a1219',
            fontWeight: 600,
            fontSize: '14px',
            cursor: sendDisabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >
          {loading ? (
            <span
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(10,18,25,0.25)',
                borderTop: '2px solid #0a1219',
                borderRadius: '50%',
                animation: 'cspin 0.8s linear infinite',
              }}
            />
          ) : (
            'Send'
          )}
        </button>
      </div>
      <div
        style={{
          maxWidth: '880px',
          margin: '8px auto 0',
          fontSize: '12px',
          color: limitHit ? '#ff7373' : 'var(--text-muted)',
          textAlign: 'right',
        }}
      >
        {usageRemaining === null ? (
          ''
        ) : limitHit ? (
          <>
            Daily limit reached ·{' '}
            <Link
              to="/upgrade"
              style={{ color: 'var(--accent)', textDecoration: 'none' }}
            >
              Upgrade to Pro
            </Link>
          </>
        ) : (
          `${usageRemaining} interaction${usageRemaining === 1 ? '' : 's'} remaining today`
        )}
      </div>
      <style>{`@keyframes cspin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
